import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import { useEffect, useState } from "react";
import { Spin } from "antd";
import { GetMultipleCongestionData } from "../../hooks/congestion";
import { Popup } from "./LayerMap";
import * as ReactDOMServer from "react-dom/server";

const heatmapGradient = {
  0.0: "red", // Strong negative congestion
  0.25: "orange", // Moderate negative congestion
  0.5: "yellow", // Neutral (zero congestion)
  0.75: "lightgreen", // Moderate positive congestion
  1.0: "green", // Strong positive congestion (available capacity)
};

export const LeafletHeatLayer = ({ points, options, onEachFeature }) => {
  const map = useMap();

  useEffect(() => {
    if (!points || !points.length) return;
    const heatLayer = L.heatLayer(
      points.map((ele) => ele.point),
      options
    ).addTo(map);
    const interactiveLayer = L.layerGroup().addTo(map);
    points.forEach((point, index) => {
      const [lat, lng, intensity] = point.point;

      // Get color based on intensity from gradient
      const gradientKeys = Object.keys(heatmapGradient)
        .map(Number)
        .sort((a, b) => a - b);
      const color =
        heatmapGradient[gradientKeys.find((key) => intensity <= key) || 1.0];

      // Create a visible circle marker that shows the congestion value
      const marker = L.circleMarker([lat, lng], {
        radius: options.radius || 25,
        color: color,
        weight: 1,
        opacity: 0,
        fillColor: color,
        fillOpacity: 0.2,
      });

      // If an onEachFeature callback is provided, invoke it.
      if (typeof onEachFeature === "function") {
        // Simulate a feature object with minimal properties.
        const feature = point;
        onEachFeature(feature, marker);
      }
      marker.addTo(interactiveLayer);
    });

    // Clean up both layers on unmount or dependency change
    return () => {
      map.removeLayer(heatLayer);
      map.removeLayer(interactiveLayer);
    };
  }, [map, points, options, onEachFeature]);

  return null;
};

export const CombinedCongestionHeatmap = ({ layers, bbox, zoom }) => {
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const style = (feature) => ({
    weight: hoveredFeature === feature ? 3 : 1.7,
    color: feature.point
      ? heatmapGradient[
          Object.keys(heatmapGradient)
            .map(Number)
            .sort((a, b) => a - b)
            .find((key) => feature.point[2] <= key) || 1.0
        ]
      : "#3388ff",
  });

  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: () => {
        setHoveredFeature(feature);
      },
      mouseout: () => {
        setHoveredFeature(null);
      },
      click: () => {
        console.log("clicked");
        setHoveredFeature(feature);
        const popupContent = ReactDOMServer.renderToString(
          <Popup
            properties={feature.properties || {}}
            layer={{ name: "Avg" }}
          />
        );
        const { coordinates } = feature.geometry;
        const [lng, lat] = coordinates; // GeoJSON format is [lng, lat]

        // Create and open the popup using the map instance
        L.popup()
          .setLatLng([lat, lng]) // Use latitude and longitude
          .setContent(popupContent)
          .openOn(layer._map); // Open the popup on the correct map instance
      },
    });
  };
  const { data: combinedData, filterChanged } = GetMultipleCongestionData();
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    filterChanged({
      keys: layers.map((layer) => layer.key).join(","),
      bbox: bbox.join(","),
      zoom,
      date: JSON.stringify(
        layers.map((layer) => ({
          key: layer.key,
          dateString: layer.dateString,
        }))
      ),
    });
  }, [layers, bbox, zoom]);

  const getHeatmapPoints = (geojsonData) => {
    if (!geojsonData?.features || geojsonData.features.length === 0) return [];

    // Extract congestion values
    const congestionValues = geojsonData.features.map(
      (feature) => feature.properties?.average_congestion_price_avg || 0
    );

    // Get min and max values
    const minValue = Math.min(...congestionValues);
    const maxValue = Math.max(...congestionValues);

    // Define a range shift so negative values are preserved in visualization
    const rangeShift = Math.abs(minValue); // Shift to make lowest value 0+
    const adjustedMax = maxValue + rangeShift; // Keep max value in proper range

    return geojsonData.features.map((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      let weight = feature.properties?.average_congestion_price_avg || 0;

      // Shift weights to maintain both negative & positive ranges
      let adjustedWeight = weight + rangeShift;

      // Normalize weight between 0 and 1 based on new adjusted range
      adjustedWeight = adjustedWeight / adjustedMax;

      return { point: [lat, lng, adjustedWeight], ...feature };
    });
  };

  return (
    <>
      {loading && <Spin spinning={loading} fullscreen />}
      {combinedData && (
        <LeafletHeatLayer
          points={getHeatmapPoints(combinedData)}
          onEachFeature={onEachFeature}
          style={style}
          options={{
            radius: 15,
            blur: 8,
            maxZoom: zoom,
            minOpacity: 0.5,
            gradient: heatmapGradient, // Apply color gradient for negative & positive values
          }}
        />
      )}
    </>
  );
};
